import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";
import { useI18n } from "vue-i18n";
import type { TNotification } from "@/api/models/message/notification";
import { usePatient } from "@/composables/models/patient/usePatient";
import { useRoomQuery } from "@/composables/models/message/useRoom";

export const useNotificationTarget = (notification: MaybeRefOrGetter<TNotification>) => {
  const { t } = useI18n();

  const roomId = computed(() => toValue(notification).data?.room ?? null);

  const directPatientId = computed(() => {
    const current = toValue(notification);
    return current.patient ?? current.data?.patient ?? null;
  });

  // A patient_message notification without a patient still references the room it was
  // created in — resolve the patient from that room's members so we can open the card.
  const shouldResolveFromRoom = computed(() => !directPatientId.value && !!roomId.value);

  const { data: linkedRoom, isLoading: isRoomLoading } = useRoomQuery({
    id: roomId,
    enabled: shouldResolveFromRoom,
  });

  const patientId = computed(
    () =>
      directPatientId.value ??
      linkedRoom?.value?.members?.find((member) => member.patient)?.patient ??
      null
  );

  const { data: linkedPatient, isLoading: isPatientLoading } = usePatient(
    {
      id: patientId,
      enabled: computed(() => !!patientId.value),
    },
    {
      suppressErrors: true,
      // Patient name/avatar shown here is static display data — keep it fresh forever so
      // switching tabs and returning doesn't refetch every card on window focus.
      staleTime: Infinity,
    }
  );

  const isReady: ComputedRef<boolean> = computed(() => {
    if (shouldResolveFromRoom.value && isRoomLoading?.value !== false) return false;
    if (!patientId.value) return true;
    return isPatientLoading?.value === false;
  });

  const patientName = computed(() =>
    `${linkedPatient?.value?.first_name ?? ""} ${linkedPatient?.value?.last_name ?? ""}`.trim()
  );

  const targetAction = computed(() => {
    if (linkedPatient?.value) {
      return {
        route: {
          name: "/rooms/patient/[id]" as const,
          params: { id: linkedPatient.value.id },
          query: roomId.value ? { room: roomId.value } : undefined,
        },
        icon: "mdi-card-account-details-outline",
        label: t("models.patient.card.title"),
      };
    }

    if (roomId.value) {
      return {
        route: { name: "/rooms/invites/[id]" as const, params: { id: roomId.value } },
        icon: "mdi-message-outline",
        label: t("generic.action.viewEntity", { entity: t("generic.entity.chat") }),
      };
    }

    return null;
  });

  return {
    roomId,
    patientId,
    linkedPatient,
    isPatientLoading,
    isReady,
    patientName,
    targetAction,
  };
};
